/**
 * PropertyOverview Component — Case Associates LP Override
 *
 * Overrides the base landing-page PropertyOverview to render the address
 * as a richtext block with the full address hyperlinked to the Google Place URL.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  useContentToken,
  useSlotGroup,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
// @ts-ignore
import { VibesitesImage } from '@vibesites/ui-primitives';
// @ts-ignore
import { ChevronLeftIcon, ChevronRightIcon, CameraIcon } from '@vibesites/assets';
// @ts-ignore
import { PhotoGallery } from '@vibesites/theme-landing-page';

// ============================================================================
// Slot group definitions for 10 gallery photos
// ============================================================================

const photo1Def: SlotGroupDefinition = {
  label: 'Gallery Photo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text', defaultValue: 'Property photo 1' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[0].description', defaultValue: '' },
  },
};

const photo2Def: SlotGroupDefinition = {
  label: 'Gallery Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text', defaultValue: 'Property photo 2' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[1].description', defaultValue: '' },
  },
};

const photo3Def: SlotGroupDefinition = {
  label: 'Gallery Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text', defaultValue: 'Property photo 3' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[2].description', defaultValue: '' },
  },
};

const photo4Def: SlotGroupDefinition = {
  label: 'Gallery Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text', defaultValue: 'Property photo 4' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[3].description', defaultValue: '' },
  },
};

const photo5Def: SlotGroupDefinition = {
  label: 'Gallery Photo 5',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text', defaultValue: 'Property photo 5' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[4].description', defaultValue: '' },
  },
};

const photo6Def: SlotGroupDefinition = {
  label: 'Gallery Photo 6',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text', defaultValue: 'Property photo 6' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[5].description', defaultValue: '' },
  },
};

const photo7Def: SlotGroupDefinition = {
  label: 'Gallery Photo 7',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[6].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[6].alt_text', defaultValue: 'Property photo 7' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[6].description', defaultValue: '' },
  },
};

const photo8Def: SlotGroupDefinition = {
  label: 'Gallery Photo 8',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[7].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[7].alt_text', defaultValue: 'Property photo 8' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[7].description', defaultValue: '' },
  },
};

const photo9Def: SlotGroupDefinition = {
  label: 'Gallery Photo 9',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[8].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[8].alt_text', defaultValue: 'Property photo 9' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[8].description', defaultValue: '' },
  },
};

const photo10Def: SlotGroupDefinition = {
  label: 'Gallery Photo 10',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[9].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[9].alt_text', defaultValue: 'Property photo 10' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[9].description', defaultValue: '' },
  },
};

const photoDefs = [photo1Def, photo2Def, photo3Def, photo4Def, photo5Def, photo6Def, photo7Def, photo8Def, photo9Def, photo10Def];
const photoKeys = [
  'propertyoverview.photo_1', 'propertyoverview.photo_2', 'propertyoverview.photo_3',
  'propertyoverview.photo_4', 'propertyoverview.photo_5', 'propertyoverview.photo_6',
  'propertyoverview.photo_7', 'propertyoverview.photo_8', 'propertyoverview.photo_9',
  'propertyoverview.photo_10',
] as const;

const GALLERY_SIZES = '(max-width: 768px) 100vw, (max-width: 1400px) 50vw, 700px';

// ============================================================================
// Component
// ============================================================================

export function PropertyOverview() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const sectionTitle = useContentToken('propertyoverview.title');
  const propertyName = useContentToken('propertyoverview.property_name');
  const phone = useContentToken('propertyoverview.phone');

  const photo1 = useSlotGroup('propertyoverview.photo_1', photo1Def);
  const photo2 = useSlotGroup('propertyoverview.photo_2', photo2Def);
  const photo3 = useSlotGroup('propertyoverview.photo_3', photo3Def);
  const photo4 = useSlotGroup('propertyoverview.photo_4', photo4Def);
  const photo5 = useSlotGroup('propertyoverview.photo_5', photo5Def);
  const photo6 = useSlotGroup('propertyoverview.photo_6', photo6Def);
  const photo7 = useSlotGroup('propertyoverview.photo_7', photo7Def);
  const photo8 = useSlotGroup('propertyoverview.photo_8', photo8Def);
  const photo9 = useSlotGroup('propertyoverview.photo_9', photo9Def);
  const photo10 = useSlotGroup('propertyoverview.photo_10', photo10Def);

  const allSlotGroups = [photo1, photo2, photo3, photo4, photo5, photo6, photo7, photo8, photo9, photo10];

  const displayGallery = useMemo(() => {
    return allSlotGroups
      .map((sg, i) => ({
        src: sg?.image || '',
        alt: sg?.alt || `Property photo ${i + 1}`,
        description: sg?.description || '',
        slotGroupKey: photoKeys[i],
        slotGroupDef: photoDefs[i],
      }))
      .filter(p => p.src);
  }, [photo1, photo2, photo3, photo4, photo5, photo6, photo7, photo8, photo9, photo10]);

  useEffect(() => {
    if (displayGallery.length === 0) return;
    const idle = window.requestIdleCallback || ((cb: IdleRequestCallback) => setTimeout(cb, 200));
    const handle = idle(() => {
      displayGallery.forEach(p => {
        if (p.src) {
          const img = new Image();
          img.src = p.src;
        }
      });
    });
    return () => {
      if (window.cancelIdleCallback) {
        window.cancelIdleCallback(handle as number);
      }
    };
  }, [displayGallery]);

  const goToSlide = useCallback((nextIdx: number) => {
    setCurrentSlide(nextIdx);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % displayGallery.length);
  }, [displayGallery.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + displayGallery.length) % displayGallery.length);
  }, [displayGallery.length]);

  const lightboxPhotos = displayGallery.map(p => ({ src: p.src, alt: p.alt }));

  return (
    <section className="bg-background py-16 md:py-24 font-body" id="overview" style={{ scrollMarginTop: 'calc(var(--header-height, 64px) + var(--offer-banner-height, 0px))' }}>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        {/* Content column */}
        <div className="flex flex-col gap-6">
          <EditableToken path="propertyoverview.title" type="text" label="Section Title" as="div">
            <h2 className="text-[clamp(1.75rem,3vw,2.5rem)] font-bold font-heading text-foreground leading-tight uppercase m-0">
              {sectionTitle}{' '}
              <EditableToken path="propertyoverview.property_name" type="text" label="Property Name" as="span">
                <span className="text-foreground">{propertyName}</span>
              </EditableToken>
              , your new home.
            </h2>
          </EditableToken>

          {/* Phone */}
          <EditableToken
            path="propertyoverview.phone"
            type="phone"
            label="Phone Number"
            as="div"
            style={{ display: 'inline-block' }}
          >
            <a href={`tel:${phone}`} className="text-foreground no-underline text-2xl font-medium font-accent">
              {phone}
            </a>
          </EditableToken>

          {/* Address — richtext with hyperlink to Google Place URL */}
          <RichTextContent
            path="propertyoverview.address"
            label="Address"
            as="div"
            className="text-muted-foreground text-base [&_a]:text-muted-foreground [&_a]:underline"
          />

          {/* Description (richtext) */}
          <RichTextContent
            path="propertyoverview.description"
            label="Description"
            as="p"
            className="text-base leading-relaxed text-foreground m-0"
          />
        </div>

        {/* Gallery column */}
        <div className="flex flex-col gap-4">
          {displayGallery.length > 0 ? (
            <>
              <div
                className="relative rounded-lg overflow-hidden aspect-[16/10] bg-neutral-900 group cursor-pointer"
                onClick={() => setLightboxOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setLightboxOpen(true); }}
                aria-label="Open photo gallery"
              >
                {/* All 10 photo slot groups rendered for editor */}
                <EditableSlotGroup groupPath="propertyoverview.photo_1" definition={photo1Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_2" definition={photo2Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_3" definition={photo3Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_4" definition={photo4Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_5" definition={photo5Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_6" definition={photo6Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_7" definition={photo7Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_8" definition={photo8Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_9" definition={photo9Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_10" definition={photo10Def}>
                  <div className="hidden" />
                </EditableSlotGroup>

                {/* All images stacked — CSS transition handles crossfade */}
                {displayGallery.map((photo, i) => (
                  <div
                    key={i}
                    className="absolute inset-0 z-[2]"
                    style={{
                      opacity: i === currentSlide ? 1 : 0,
                      transition: 'opacity 0.35s ease',
                      pointerEvents: i === currentSlide ? 'auto' : 'none',
                    }}
                  >
                    <VibesitesImage
                      url={photo.src}
                      alt={photo.alt || 'Property image'}
                      fill
                      objectFit="cover"
                      sizes={GALLERY_SIZES}
                      quality={80}
                      priority={i < 3}
                    />
                  </div>
                ))}


                {/* "View gallery" badge */}
                <div className="absolute top-3 right-3 z-[4] flex items-center gap-1.5 bg-black/50 backdrop-blur-sm text-white text-xs font-medium font-body px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  <CameraIcon size={13} />
                  <span>{displayGallery.length} Photos</span>
                </div>

                {/* Overlay navigation buttons */}
                {displayGallery.length > 1 && (
                  <>
                    <button
                      className="absolute top-1/2 left-3 -translate-y-1/2 z-[5] w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm shadow-sm cursor-pointer flex items-center justify-center transition-all duration-200 hover:bg-white hover:shadow-md border-none opacity-70 md:opacity-0 md:group-hover:opacity-100 text-foreground"
                      onClick={(e) => { e.stopPropagation(); prevSlide(); }}
                      aria-label="Previous image"
                    >
                      <ChevronLeftIcon size={18} />
                    </button>
                    <button
                      className="absolute top-1/2 right-3 -translate-y-1/2 z-[5] w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm shadow-sm cursor-pointer flex items-center justify-center transition-all duration-200 hover:bg-white hover:shadow-md border-none opacity-70 md:opacity-0 md:group-hover:opacity-100 text-foreground"
                      onClick={(e) => { e.stopPropagation(); nextSlide(); }}
                      aria-label="Next image"
                    >
                      <ChevronRightIcon size={18} />
                    </button>
                  </>
                )}
              </div>

              {/* Dot indicators + counter */}
              {displayGallery.length > 1 && (
                <div className="flex items-center justify-center gap-3">
                  <div className="flex items-center gap-1.5">
                    {displayGallery.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className="w-2 h-2 rounded-full border-none cursor-pointer p-0"
                        style={{
                          backgroundColor: index === currentSlide ? 'var(--vibe-color-primary)' : 'var(--vibe-color-border, #d1d5db)',
                          transform: index === currentSlide ? 'scale(1.25)' : 'scale(1)',
                          transition: 'background-color 0.3s ease, transform 0.3s ease',
                        }}
                        aria-label={`Go to image ${index + 1}`}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {currentSlide + 1} / {displayGallery.length}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="aspect-[16/10] bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
              <span>No gallery images available</span>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxPhotos.length > 0 && (
        <PhotoGallery
          photos={lightboxPhotos}
          open={lightboxOpen}
          initialIndex={currentSlide}
          onClose={() => setLightboxOpen(false)}
        />
      )}

    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.title': {
      label: 'Section Title',
      type: 'text',
      defaultValue: 'Welcome to',
    },
    'propertyoverview.property_name': {
      label: 'Property Name',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Your New Home',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_short_description',
      defaultValue: 'Discover modern living at its finest.',
    },
    'propertyoverview.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_phone',
      defaultValue: '555-555-5555',
    },
    'propertyoverview.address': {
      label: 'Address',
      type: 'richtext',
      defaultValue: '<p><a data-field-href="property_google_place_url"><span data-type="field-token" data-field="property_full_address"></span></a></p>',
    },
  },
  slotGroups: {
    'propertyoverview.photo_1': {
      label: 'Gallery Photo 1',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text', defaultValue: 'Property photo 1' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[0].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_2': {
      label: 'Gallery Photo 2',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text', defaultValue: 'Property photo 2' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[1].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_3': {
      label: 'Gallery Photo 3',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text', defaultValue: 'Property photo 3' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[2].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_4': {
      label: 'Gallery Photo 4',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text', defaultValue: 'Property photo 4' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[3].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_5': {
      label: 'Gallery Photo 5',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text', defaultValue: 'Property photo 5' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[4].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_6': {
      label: 'Gallery Photo 6',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text', defaultValue: 'Property photo 6' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[5].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_7': {
      label: 'Gallery Photo 7',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[6].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[6].alt_text', defaultValue: 'Property photo 7' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[6].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_8': {
      label: 'Gallery Photo 8',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[7].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[7].alt_text', defaultValue: 'Property photo 8' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[7].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_9': {
      label: 'Gallery Photo 9',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[8].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[8].alt_text', defaultValue: 'Property photo 9' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[8].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_10': {
      label: 'Gallery Photo 10',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[9].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[9].alt_text', defaultValue: 'Property photo 10' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[9].description', defaultValue: '' },
      },
    },
  },
};

(PropertyOverview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyOverview;
